/**
 * User Permissions Query Hook
 *
 * Uses TanStack Query to fetch and cache user permissions.
 */

import { useQuery } from '@tanstack/react-query';
import useApiManager from '../../../hooks/useApiManager';
import type { GetLicenseResponse } from '../../../types';
import { useUser } from '../../UserContext';
import type { UserPermissions, UserPermissionsProviderOptions } from '../types';

/**
 * Query keys for user permissions
 */
export const USER_PERMISSIONS_QUERY_KEY = ['user', 'permissions'] as const;

/**
 * Hook to fetch user permissions using React Query
 */
export const useUserPermissionsQuery = (options: UserPermissionsProviderOptions) => {
  const { isAuthenticated, idToken } = useUser();
  const apiManager = useApiManager();

  return useQuery<UserPermissions, Error>({
    queryKey: USER_PERMISSIONS_QUERY_KEY,
    queryFn: async () => {
      if (!idToken) {
        throw new Error('No ID token available');
      }

      const licenseInfo: GetLicenseResponse = await apiManager.licensing
        .getLicense(idToken)
        .catch((error) => {
          console.error('Failed to fetch license info:', error);
          return { license_type: 'null', license_data: { products: [], items: {} } };
        });

      return {
        license: licenseInfo,
        canCreateRooms: licenseInfo.license_type !== 'null',
        canScheduleMeetings: licenseInfo.license_type !== 'anon',
        hasValidLicense: ['paid', 'free'].includes(licenseInfo.license_type),
      };
    },
    enabled: isAuthenticated && !!idToken,
    staleTime: options.staleTime,
    gcTime: options.cacheTime,
  });
};
